// Dosya: ManualEntryPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Edit,
    Trash,
    PlusCircle,
    CheckCircle,
    ShieldAlert,
    AlertCircle,
    RefreshCw,
    Download,
    Filter,
    Calendar,
    Building2,
    ArrowRightCircle,
    ArrowLeftCircle,
    Edit3,
    Radio,
    X
} from 'lucide-react';
import { apiClient } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import PassRecordForm from './PassRecordForm';
import DataTable from '../components/common/DataTable';
import InfoModal from '../components/common/InfoModal';

const toDateInputValue = (dateObj) => {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export default function ManualEntryPage() {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    const { permissions } = useAuth();
    const canView = useMemo(() => permissions.includes('pass_records:view_list'), [permissions]);
    const canManage = useMemo(() => permissions.includes('pass_records:manage_manual'), [permissions]);

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [formError, setFormError] = useState(null);
    const [modalMode, setModalMode] = useState('add');
    const [itemToDelete, setItemToDelete] = useState(null);

    // Filter lookups
    const [departments, setDepartments] = useState([]);

    // Top filters
    const [datePreset, setDatePreset] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [departmentId, setDepartmentId] = useState('');
    const [recordTypeFilter, setRecordTypeFilter] = useState('');

    // Server-side table params
    const [serverParams, setServerParams] = useState({
        page: 1,
        itemsPerPage: 20,
        searchTerm: '',
        columnFilters: {},
        sortColumn: 'recordTime',
        sortDirection: 'desc'
    });

    const [totalRows, setTotalRows] = useState(0);
    const [summary, setSummary] = useState({
        totalCount: 0,
        entryCount: 0,
        exitCount: 0
    });

    // Auto-hide success banner after 5 seconds
    useEffect(() => {
        if (!message) return;
        const t = setTimeout(() => setMessage(null), 5000);
        return () => clearTimeout(t);
    }, [message]);

    useEffect(() => {
        if (!canView) return;
        let isMounted = true;
        apiClient('passrecord/form-lookups')
            .then((res) => {
                if (isMounted && res?.departments) {
                    setDepartments(res.departments);
                }
            })
            .catch(() => {});
        return () => {
            isMounted = false;
        };
    }, [canView]);

    const applyDatePreset = (preset) => {
        setDatePreset(preset);
        const now = new Date();
        if (preset === 'today') {
            const todayStr = toDateInputValue(now);
            setStartDate(todayStr);
            setEndDate(todayStr);
        } else if (preset === 'yesterday') {
            const y = new Date(now);
            y.setDate(y.getDate() - 1);
            const yStr = toDateInputValue(y);
            setStartDate(yStr);
            setEndDate(yStr);
        } else if (preset === 'last7') {
            const start = new Date(now);
            start.setDate(start.getDate() - 6);
            setStartDate(toDateInputValue(start));
            setEndDate(toDateInputValue(now));
        } else if (preset === 'thisMonth') {
            const start = new Date(now.getFullYear(), now.getMonth(), 1);
            setStartDate(toDateInputValue(start));
            setEndDate(toDateInputValue(now));
        } else {
            setStartDate('');
            setEndDate('');
        }
    };

    const resetTopFilters = () => {
        setDatePreset('all');
        setStartDate('');
        setEndDate('');
        setDepartmentId('');
        setRecordTypeFilter('');
    };

    const hasActiveTopFilters = Boolean(startDate || endDate || departmentId || recordTypeFilter);

    const fetchData = useCallback(async () => {
        if (!canView) {
            setError('Manuel kayıtları görüntüleme yetkiniz yok.');
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const colFilters = serverParams.columnFilters || {};
            const combinedSearchParts = [
                serverParams.searchTerm,
                colFilters.employeeFullName,
                colFilters.deviceSerialNumber,
                colFilters.notes,
                colFilters.olusturanKullaniciAdi
            ].filter(Boolean);
            const effectiveSearch = combinedSearchParts.join(' ').trim();

            const queryParams = new URLSearchParams({
                paged: 'true',
                page: String(serverParams.page || 1),
                pageSize: String(serverParams.itemsPerPage || 20)
            });

            if (effectiveSearch) queryParams.set('searchTerm', effectiveSearch);
            if (startDate) queryParams.set('startDate', startDate);
            if (endDate) queryParams.set('endDate', endDate);
            if (departmentId) queryParams.set('departmentId', departmentId);
            if (recordTypeFilter) queryParams.set('recordType', recordTypeFilter);
            if (serverParams.sortColumn) queryParams.set('sortBy', serverParams.sortColumn);
            if (serverParams.sortDirection) queryParams.set('sortDir', serverParams.sortDirection);

            const response = await apiClient(`passrecord/manual?${queryParams.toString()}`);
            const items = Array.isArray(response) ? response : response?.items || [];
            const total = Array.isArray(response) ? response.length : response?.totalCount ?? 0;

            setRecords(items);
            setTotalRows(total);
            setSummary({
                totalCount: total,
                entryCount: response?.entryCount ?? items.filter((x) => x.recordType === 'Entry' || x.recordType === 0).length,
                exitCount: response?.exitCount ?? items.filter((x) => x.recordType === 'Exit' || x.recordType === 1).length
            });
        } catch (err) {
            setError(err.message || 'Manuel geçiş kayıtları yüklenemedi.');
        } finally {
            setLoading(false);
        }
    }, [canView, serverParams, startDate, endDate, departmentId, recordTypeFilter]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleServerParamsChange = useCallback((params) => {
        setServerParams(params);
    }, []);

    const handleOpenModal = (mode, item = null) => {
        setModalMode(mode);
        setCurrentItem(item);
        setFormError(null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setFormError(null);
    };

    const handleSaveRecord = async (formData) => {
        if (!canManage) {
            setFormError('Manuel kayıt yönetme yetkiniz bulunmamaktadır.');
            return;
        }
        setIsSubmitting(true);
        setFormError(null);
        const isNew = modalMode === 'add';

        try {
            if (isNew && formData.isBulk) {
                const res = await apiClient('passrecord/bulk', {
                    method: 'POST',
                    body: {
                        employeeIds: formData.employeeIds,
                        recordTime: formData.recordTime,
                        recordType: formData.recordType,
                        deviceSerialNumber: formData.deviceSerialNumber,
                        deviceId: formData.deviceId,
                        notes: formData.notes
                    }
                });
                setMessage(res?.message || 'Toplu manuel geçiş kayıtları başarıyla oluşturuldu.');
            } else {
                const url = isNew ? 'passrecord' : `passrecord/${currentItem.id}`;
                const method = isNew ? 'POST' : 'PUT';
                await apiClient(url, { method, body: formData });
                setMessage(`Manuel geçiş kaydı başarıyla ${isNew ? 'oluşturuldu' : 'güncellendi'}.`);
            }
            handleCloseModal();
            fetchData();
        } catch (err) {
            setFormError(err.message || 'Kayıt işlemi sırasında bir hata oluştu.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteClick = (item) => setItemToDelete(item);

    const confirmDelete = async () => {
        if (!canManage) {
            setError('Manuel kayıt silme yetkiniz bulunmamaktadır.');
            setItemToDelete(null);
            return;
        }
        if (!itemToDelete) return;
        setIsSubmitting(true);
        try {
            await apiClient(`passrecord/${itemToDelete.id}`, { method: 'DELETE' });
            setMessage('Manuel geçiş kaydı başarıyla silindi.');
            fetchData();
        } catch (err) {
            setError(err.message || 'Kayıt silinirken bir hata oluştu.');
        } finally {
            setItemToDelete(null);
            setIsSubmitting(false);
        }
    };

    const handleCancelDelete = () => setItemToDelete(null);

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const handleExportCsv = () => {
        if (!records || records.length === 0) return;
        const headers = [
            'Sicil No',
            'Personel Ad Soyad',
            'Departman',
            'Geçiş Zamanı',
            'Yön',
            'Geçiş Noktası / Okuyucu',
            'Notlar / Gerekçe',
            'Oluşturan',
            'Oluşturulma Tarihi',
            'Güncelleyen',
            'Güncellenme Tarihi'
        ];
        const rows = records.map((r) => {
            const isEntry = r.recordType === 'Entry' || r.recordType === 0;
            return [
                r.employeeRegistrationNumber || '',
                r.employeeFullName || '',
                r.departmentName || '',
                formatDate(r.recordTime),
                isEntry ? 'Giriş' : 'Çıkış',
                r.deviceName || r.deviceSerialNumber || 'Manuel',
                r.notes || '',
                r.olusturanKullaniciAdi || '',
                formatDate(r.createdDate),
                r.guncelleyenKullaniciAdi || '',
                formatDate(r.updatedDate)
            ];
        });

        const csvContent =
            '\uFEFF' +
            [headers, ...rows]
                .map((row) =>
                    row
                        .map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`)
                        .join(';')
                )
                .join('\r\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `manuel-gecis-kayitlari-${toDateInputValue(new Date())}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const columns = useMemo(() => {
        const cols = [
            {
                header: 'Personel',
                accessor: 'employeeFullName',
                sortable: true,
                searchable: true,
                width: '23%',
                render: (row) => (
                    <div className="flex flex-col">
                        <span className="font-semibold text-gray-900 dark:text-white">
                            {row.employeeFullName || '-'}
                        </span>
                        <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                            {row.employeeRegistrationNumber && (
                                <span className="text-[11px] font-mono px-1.5 py-0.2 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                    #{row.employeeRegistrationNumber}
                                </span>
                            )}
                            {row.departmentName && (
                                <span className="text-[11px] text-gray-500 dark:text-gray-400">
                                    • {row.departmentName}
                                </span>
                            )}
                        </div>
                    </div>
                )
            },
            {
                header: 'Geçiş Zamanı',
                accessor: 'recordTime',
                sortable: true,
                filterable: false,
                width: '15%',
                render: (row) => (
                    <span className="font-mono text-xs sm:text-sm font-medium text-gray-800 dark:text-gray-100">
                        {formatDate(row.recordTime)}
                    </span>
                )
            },
            {
                header: 'Yön / Tip',
                accessor: 'recordType',
                sortable: true,
                filterable: false,
                width: '11%',
                render: (row) => {
                    const isEntry = row.recordType === 'Entry' || row.recordType === 0;
                    return (
                        <span
                            className={`px-2.5 py-0.5 inline-flex items-center gap-1 text-xs font-semibold rounded-full ${
                                isEntry
                                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-800'
                                    : 'bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-200 border border-rose-200 dark:border-rose-800'
                            }`}
                        >
                            {isEntry ? <ArrowRightCircle size={13} /> : <ArrowLeftCircle size={13} />}
                            {isEntry ? 'Giriş' : 'Çıkış'}
                        </span>
                    );
                }
            },
            {
                header: 'Geçiş Noktası / Okuyucu',
                accessor: 'deviceSerialNumber',
                sortable: true,
                searchable: true,
                width: '19%',
                render: (row) => {
                    const resolvedName = row.deviceName || row.deviceSerialNumber;
                    if (!resolvedName) {
                        return <span className="text-xs text-gray-400 italic">Manuel (Belirtilmedi)</span>;
                    }
                    const showRaw = row.deviceName && row.deviceSerialNumber && row.deviceName !== row.deviceSerialNumber;
                    return (
                        <div className="flex flex-col">
                            <span className="font-medium text-gray-800 dark:text-gray-100 flex items-center gap-1.5">
                                <Radio size={13} className="text-indigo-500 shrink-0" />
                                <span>{resolvedName}</span>
                            </span>
                            {showRaw && (
                                <span className="text-[11px] font-mono text-gray-400 dark:text-gray-500 mt-0.5">
                                    {row.deviceSerialNumber}
                                </span>
                            )}
                        </div>
                    );
                }
            },
            {
                header: 'Notlar / Gerekçe',
                accessor: 'notes',
                sortable: true,
                searchable: true,
                width: '15%',
                render: (row) => (
                    <div className="max-w-xs">
                        {row.notes ? (
                            <span className="text-xs text-gray-700 dark:text-gray-300 whitespace-normal line-clamp-2" title={row.notes}>
                                {row.notes}
                            </span>
                        ) : (
                            <span className="text-xs text-gray-400">-</span>
                        )}
                    </div>
                )
            },
            {
                header: 'Oluşturan / İşlem Tarihi',
                accessor: 'olusturanKullaniciAdi',
                sortable: true,
                searchable: true,
                width: '11%',
                render: (row) => (
                    <div className="flex flex-col items-start gap-0.5">
                        <span className="px-2 py-0.5 inline-flex text-[11px] font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                            {row.olusturanKullaniciAdi || '-'}
                        </span>
                        {row.createdDate && (
                            <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500">
                                {formatDate(row.createdDate)}
                            </span>
                        )}
                        {row.guncelleyenKullaniciAdi && (
                            <span
                                className="text-[10px] text-amber-600 dark:text-amber-400"
                                title={row.updatedDate ? `Güncellenme: ${formatDate(row.updatedDate)}` : ''}
                            >
                                ✎ {row.guncelleyenKullaniciAdi}
                            </span>
                        )}
                    </div>
                )
            }
        ];

        if (canManage) {
            cols.push({
                header: 'İşlem',
                accessor: 'actions',
                filterable: false,
                width: '6%',
                render: (row) => (
                    <div className="flex items-center justify-end gap-1">
                        <button
                            type="button"
                            onClick={() => handleOpenModal('edit', row)}
                            className="p-1.5 rounded-lg text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
                            title="Düzenle"
                        >
                            <Edit className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() => handleDeleteClick(row)}
                            className="p-1.5 rounded-lg text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 transition-colors"
                            title="Sil"
                        >
                            <Trash className="w-4 h-4" />
                        </button>
                    </div>
                )
            });
        }
        return cols;
    }, [canManage]);

    if (!loading && !canView) {
        return (
            <div className="p-6 bg-red-50 dark:bg-red-900/10 rounded-lg shadow-md text-center">
                <ShieldAlert className="mx-auto h-12 w-12 text-red-400" />
                <h3 className="mt-2 text-lg font-medium text-red-800 dark:text-red-300">Yetki Hatası</h3>
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">Bu sayfayı görüntüleme yetkiniz bulunmamaktadır.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4 p-1">
            {message && (
                <div className="bg-emerald-50 dark:bg-emerald-900/40 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 p-3.5 rounded-xl flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-2 text-sm font-medium">
                        <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />
                        <span>{message}</span>
                    </div>
                    <button
                        type="button"
                        onClick={() => setMessage(null)}
                        className="text-emerald-600 dark:text-emerald-300 hover:opacity-75"
                    >
                        <X size={16} />
                    </button>
                </div>
            )}

            {/* Sayfa Başlığı ve Üst Aksiyonlar */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border border-gray-200/70 dark:border-gray-700/70">
                <div>
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Edit3 className="text-blue-600 dark:text-blue-400" size={22} />
                        Manuel Geçiş Kayıtları
                    </h1>
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        Yetkili kullanıcılar tarafından elle eklenen tekli veya toplu personel geçiş kayıtları
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-2 self-end sm:self-auto">
                    <button
                        type="button"
                        onClick={fetchData}
                        disabled={loading}
                        className="px-3.5 py-2 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg flex items-center gap-1.5 transition-colors disabled:opacity-50"
                    >
                        <RefreshCw size={15} className={loading ? 'animate-spin text-blue-500' : ''} />
                        Yenile
                    </button>
                    <button
                        type="button"
                        onClick={handleExportCsv}
                        disabled={records.length === 0}
                        className="px-3.5 py-2 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg flex items-center gap-1.5 transition-colors disabled:opacity-50"
                    >
                        <Download size={15} />
                        CSV / Excel İndir
                    </button>
                    {canManage && (
                        <button
                            type="button"
                            onClick={() => handleOpenModal('add')}
                            className="px-4 py-2 text-xs sm:text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-sm flex items-center gap-1.5 transition-colors"
                        >
                            <PlusCircle className="w-4 h-4" />
                            Yeni Manuel Kayıt Ekle
                        </button>
                    )}
                </div>
            </div>

            {/* KPI Özet Kartları */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                    type="button"
                    onClick={() => setRecordTypeFilter('')}
                    className={`text-left p-4 rounded-xl border transition-all ${
                        !recordTypeFilter
                            ? 'bg-blue-50/80 dark:bg-blue-900/25 border-blue-400 dark:border-blue-600 shadow-sm'
                            : 'bg-white dark:bg-gray-800 border-gray-200/70 dark:border-gray-700/70 hover:border-blue-300'
                    }`}
                >
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>Toplam Manuel Kayıt</span>
                        <Edit3 size={16} className="text-blue-500" />
                    </div>
                    <div className="text-xl font-bold text-gray-900 dark:text-white mt-1">
                        {summary.totalCount.toLocaleString('tr-TR')}
                    </div>
                </button>

                <button
                    type="button"
                    onClick={() => setRecordTypeFilter((prev) => (prev === 'Entry' ? '' : 'Entry'))}
                    className={`text-left p-4 rounded-xl border transition-all ${
                        recordTypeFilter === 'Entry'
                            ? 'bg-emerald-50 dark:bg-emerald-900/25 border-emerald-400 dark:border-emerald-600 shadow-sm'
                            : 'bg-white dark:bg-gray-800 border-gray-200/70 dark:border-gray-700/70 hover:border-emerald-300'
                    }`}
                >
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>Manuel Giriş Kayıtları</span>
                        <ArrowRightCircle size={16} className="text-emerald-500" />
                    </div>
                    <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                        {summary.entryCount.toLocaleString('tr-TR')}
                    </div>
                </button>

                <button
                    type="button"
                    onClick={() => setRecordTypeFilter((prev) => (prev === 'Exit' ? '' : 'Exit'))}
                    className={`text-left p-4 rounded-xl border transition-all ${
                        recordTypeFilter === 'Exit'
                            ? 'bg-rose-50 dark:bg-rose-900/25 border-rose-400 dark:border-rose-600 shadow-sm'
                            : 'bg-white dark:bg-gray-800 border-gray-200/70 dark:border-gray-700/70 hover:border-rose-300'
                    }`}
                >
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>Manuel Çıkış Kayıtları</span>
                        <ArrowLeftCircle size={16} className="text-rose-500" />
                    </div>
                    <div className="text-xl font-bold text-rose-600 dark:text-rose-400 mt-1">
                        {summary.exitCount.toLocaleString('tr-TR')}
                    </div>
                </button>
            </div>

            {/* Filtre Barı */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200/70 dark:border-gray-700/70 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        <Filter size={14} className="text-blue-500" />
                        Tarih & Departman Filtreleri
                    </div>

                    <div className="flex flex-wrap items-center gap-1.5">
                        {[
                            { id: 'today', label: 'Bugün' },
                            { id: 'yesterday', label: 'Dün' },
                            { id: 'last7', label: 'Son 7 Gün' },
                            { id: 'thisMonth', label: 'Bu Ay' },
                            { id: 'all', label: 'Tüm Zamanlar' }
                        ].map((preset) => (
                            <button
                                key={preset.id}
                                type="button"
                                onClick={() => applyDatePreset(preset.id)}
                                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                                    datePreset === preset.id
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                            >
                                {preset.label}
                            </button>
                        ))}

                        {hasActiveTopFilters && (
                            <button
                                type="button"
                                onClick={resetTopFilters}
                                className="ml-2 px-2.5 py-1 text-xs font-medium text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/20 hover:bg-rose-100 dark:hover:bg-rose-900/40 rounded-md flex items-center gap-1"
                            >
                                <X size={13} /> Filtreleri Sıfırla
                            </button>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 pt-1">
                    <div>
                        <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
                            <Calendar size={12} /> Başlangıç Tarihi
                        </label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => {
                                setStartDate(e.target.value);
                                setDatePreset('custom');
                            }}
                            className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
                            <Calendar size={12} /> Bitiş Tarihi
                        </label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => {
                                setEndDate(e.target.value);
                                setDatePreset('custom');
                            }}
                            className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
                            <Building2 size={12} /> Departman
                        </label>
                        <select
                            value={departmentId}
                            onChange={(e) => setDepartmentId(e.target.value)}
                            className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Tüm Departmanlar</option>
                            {departments.map((d) => (
                                <option key={d.id} value={d.id}>
                                    {d.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                            Geçiş Yönü
                        </label>
                        <select
                            value={recordTypeFilter}
                            onChange={(e) => setRecordTypeFilter(e.target.value)}
                            className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Tümü (Giriş & Çıkış)</option>
                            <option value="Entry">Sadece Giriş</option>
                            <option value="Exit">Sadece Çıkış</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Veri Tablosu */}
            <DataTable
                data={records}
                columns={columns}
                itemsPerPage={serverParams.itemsPerPage}
                showSearch={true}
                searchPlaceholder="Personel adı, sicil no, okuyucu adı, notlar veya oluşturan kullanıcıda ara..."
                emptyMessage="Seçilen kriterlere uygun manuel geçiş kaydı bulunamadı."
                isLoading={loading}
                errorMessage={error}
                serverSide={true}
                totalRows={totalRows}
                onServerParamsChange={handleServerParamsChange}
            />

            {/* Ekleme / Düzenleme Modalı */}
            <InfoModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={modalMode === 'add' ? 'Yeni Manuel Geçiş Kaydı Ekle' : 'Manuel Geçiş Kaydını Düzenle'}
                showFooter={false}
                className="max-w-2xl"
            >
                <PassRecordForm
                    record={currentItem}
                    isNew={modalMode === 'add'}
                    onSave={handleSaveRecord}
                    onCancel={handleCloseModal}
                    submitting={isSubmitting}
                    formError={formError}
                />
            </InfoModal>

            {/* Silme Onay Modalı */}
            <InfoModal
                isOpen={!!itemToDelete}
                onClose={handleCancelDelete}
                title="Manuel Kaydı Silme Onayı"
                message={
                    itemToDelete
                        ? `"${itemToDelete.employeeFullName}" personeline ait ${formatDate(
                              itemToDelete.recordTime
                          )} tarihli manuel geçiş kaydını kalıcı olarak silmek istediğinizden emin misiniz?`
                        : 'Bu manuel kaydı kalıcı olarak silmek istediğinizden emin misiniz?'
                }
                type="confirm"
                onConfirm={confirmDelete}
                confirmText="Evet, Sil"
                cancelText="İptal"
                isLoading={isSubmitting}
            />
        </div>
    );
}
